import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { MonitorSmartphone, Play } from 'lucide-react'

import routes from 'constants/routes'
import { cn } from 'lib/utils'

import { presentationEditPath, presenterViewPath } from '../../featureFlag'
import { useAssetNames } from '../../hooks/useAssetNames'
import { IMAGE_WAIT_MS, usePresentationResolver } from '../../hooks/usePresentationResolver'
import { BlockReadinessProvider, useReadinessTracker } from '../../blocks/readiness'
import { usePresentationSync } from '../../hooks/usePresentationSync'
import { indexOfSlide, presentationSlides, startIndexForAnchor, storySpans, transitionFor, useKeepSlideInPlace, usePresenter } from '../../hooks/usePresenter'
import { STAGE_HEIGHT, STAGE_WIDTH } from '../../layouts'
import * as presentationService from '../../services/presentationService'
import type { AssetName } from '../../hooks/useAssetNames'
import { SharedPresentationLoadError } from '../../services/presentationService'
import type { EntityRef, Presentation, SharedPresentation, SharedPresentationError, Snapshot } from '../../types/presentation.types'
import { OverviewGrid } from './OverviewGrid'
import { PageFlip } from './PageFlip'
import { PresentedSlideView } from './PresentedSlideView'
import { PresenterChrome } from './PresenterChrome'
import { SpeakerNotesOverlay } from './SpeakerNotesOverlay'
import { StoryMenu } from './StoryMenu'
import { TouchViewer } from './TouchViewer'
import { viewerKind } from '../../utils/viewerKind'

/**
 * Full-screen presenting (MYD-496), with stories, notes and the presenter view
 * (MYD-497). The point of the whole feature: present from inside the app, like
 * a slideshow, with nothing to fumble.
 *
 * ## Getting ready
 *
 * Opening a deck shows "Preparing..." while the story being started on is
 * resolved and its images preloaded, and while the first slide's maps load
 * their tiles off-screen (MYD-515, at most a few seconds), then a Start
 * button. The button is not ceremony: browsers only grant full screen in
 * response to a user gesture.
 * Once running, the next story resolves in the background, and the next slide
 * is mounted off-screen so its images (and, from MYD-500, map tiles) are warm
 * by the time of the flip.
 *
 * ## The stage
 *
 * The screen is black. The 1280x720 slide is scaled to the largest 16:9 frame
 * that fits and centred in it, and transitions animate inside that frame, so a
 * page turn pivots on the slide's own edge rather than the screen's.
 *
 * Heights use `dvh`, not `vh`: on iOS Safari `100vh` includes the area under
 * the toolbar, and a bottom-anchored control there is invisible on hardware
 * even though it measures correctly in an emulator (see CLAUDE.md).
 *
 * ## Two windows
 *
 * P (or the button) opens the presenter view in a second window: notes, the
 * next slide, a timer. The two stay on the same slide over BroadcastChannel,
 * and either can drive. Opening a window can drop the browser out of full
 * screen, which must NOT be read as the Esc that ends the show, so that exit
 * is suppressed for a moment around `window.open`.
 *
 * ## Leaving
 *
 * Esc exits to wherever the presentation was launched from (`location.state
 * .from`), else the editor for an owner or the gallery for anyone else. In
 * full screen the browser swallows Esc to leave full screen, so leaving the
 * full screen WE entered is treated as Esc too: one press, out.
 */

const CHROME_HIDE_MS = 2000
const NOTICE_MS = 4000
const SWIPE_MIN_PX = 50
/** How long after opening the presenter view a full-screen exit is not an Esc. */
const POPUP_GRACE_MS = 1500

const fullscreenAvailable = () => typeof document !== 'undefined' && Boolean(document.documentElement.requestFullscreen) && document.fullscreenEnabled !== false

const prefersReducedMotion = () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true

/** The largest 16:9 frame that fits the box. */
const fitFrame = (width: number, height: number) => {
	const scale = Math.min(width / STAGE_WIDTH, height / STAGE_HEIGHT)
	return { width: Math.floor(STAGE_WIDTH * scale), height: Math.floor(STAGE_HEIGHT * scale) }
}

/** The words a recipient sees when a shared link will not open (MYD-509). */
const SHARE_ERRORS: Record<SharedPresentationError, string> = {
	not_found: 'This link does not exist. Check that it was copied in full.',
	expired: 'This link has expired. Ask the person who shared it for a new one.',
	revoked: 'This link has been turned off by the person who shared it.',
	unavailable: 'This presentation is no longer available.',
	rate_limited: 'Too many attempts. Wait a minute and try again.',
	failed: 'This presentation could not be opened. Try again in a moment.',
}

interface PresenterScreenProps {
	/**
	 * A public share link's token (MYD-509). The deck then comes from the
	 * link's snapshot, signed out, and nothing is fetched as a user: no
	 * resolve, no asset names, no "presented" record, no presenter view. Leaving
	 * returns to the start screen, there being nowhere in the app to go.
	 */
	publicToken?: string
}

export function PresenterScreen({ publicToken }: PresenterScreenProps = {}) {
	const { id = '' } = useParams()
	const [shared, setShared] = useState<SharedPresentation | null>(null)
	const [loadError, setLoadError] = useState<SharedPresentationError | null>(null)
	const navigate = useNavigate()
	const location = useLocation()
	const [params] = useSearchParams()
	// Decided once (MYD-498): rotating a tablet must not swap the presenter
	// out from under the audience.
	const [viewer] = useState(() => viewerKind(params.get('viewer')))

	const [presentation, setPresentation] = useState<Presentation | null>(null)
	const [status, setStatus] = useState<'loading' | 'failed' | 'ready'>('loading')
	const [started, setStarted] = useState(false)
	const [chromeVisible, setChromeVisible] = useState(true)
	const [fullscreen, setFullscreen] = useState(false)
	const [reducedMotion, setReducedMotion] = useState(prefersReducedMotion)
	const [frame, setFrame] = useState({ width: 0, height: 0 })
	const [notice, setNotice] = useState<string | null>(null)

	// Captured once, on arrival. Keeping ?slide= in step replaces the history
	// entry, and a replace would otherwise drop the router state carrying it.
	const launchedFrom = useRef((location.state as { from?: string } | null)?.from ?? null)
	// `?anchor=location:<id>`: "Present" from an asset's page starts at its story.
	const anchor = useRef(params.get('anchor'))
	// `?snapshot=3`: show published version 3 (MYD-508) instead of the live
	// deck: its frozen definition and blocks, nothing resolved.
	const snapshotVersion = useRef(Number(params.get('snapshot')) > 0 ? Number(params.get('snapshot')) : null)
	const [snapshot, setSnapshot] = useState<Snapshot | null>(null)

	const rootRef = useRef<HTMLDivElement>(null)
	const enteredFullscreen = useRef(false)
	const togglingFullscreen = useRef(false)
	const openingPopupUntil = useRef(0)
	const swipeStart = useRef<{ x: number; y: number } | null>(null)

	const deck = snapshot?.definition ?? presentation?.definition ?? null
	// The story on screen, as of the last render. State rather than derived,
	// because the slide list now depends on what has resolved (continuation
	// slides, MYD-504) and what to resolve depends on the story on screen.
	const [currentStory, setCurrentStory] = useState<number | null>(null)
	const wantedStories = useMemo(() => (currentStory === null ? [] : [currentStory, currentStory + 1]), [currentStory])
	const resolver = usePresentationResolver(presentation && !snapshot ? id : null, deck, wantedStories, snapshot?.blocks)
	const slides = useMemo(() => (deck ? presentationSlides(deck, resolver.blocks) : []), [deck, resolver.blocks])
	const spans = useMemo(() => storySpans(slides), [slides])
	// Only the deck's own slides decide where to start; continuations come later.
	const startAt = useMemo(() => (deck ? startIndexForAnchor(presentationSlides(deck), anchor.current) : null), [deck])
	const presenter = usePresenter(slides.length, startAt)
	useKeepSlideInPlace(slides, presenter)
	const storyOnScreen = slides[presenter.index]?.storyIndex ?? null
	useEffect(() => setCurrentStory(storyOnScreen), [storyOnScreen])
	// Divider names: frozen in the snapshot when it has them (MYD-509), live
	// otherwise. A public viewer could not look them up, and should not.
	const frozenAssets = snapshot?.assets
	const liveAssetName = useAssetNames(deck && !frozenAssets ? deck.stories.map(story => (story.divider ? story.anchor : null)) : [])
	const assetName = useCallback(
		(ref: EntityRef | null | undefined): AssetName | null => (frozenAssets ? (ref ? frozenAssets[`${ref.scope}:${ref.entityId}`] ?? null : null) : liveAssetName(ref)),
		[frozenAssets, liveAssetName],
	)

	// --- Ready to start (MYD-515) -------------------------------------------
	// The story's data and images are not the whole of it: a map is blank until
	// its tiles are in. So while preparing, the first slide is drawn off-screen
	// (see ReadinessProbe below), its maps report through the readiness
	// context, and Start waits for them, or for IMAGE_WAIT_MS, whichever comes
	// first. A slide with no map waits for nothing but the probe's own mount.
	// Keyed by slide, so a result for one slide never stands in for another.
	const readiness = useReadinessTracker()
	const [probedSlide, setProbedSlide] = useState<string | null>(null)
	const [probeTimedOut, setProbeTimedOut] = useState<string | null>(null)
	useEffect(() => {
		if (!probedSlide) return
		const timer = setTimeout(() => setProbeTimedOut(probedSlide), IMAGE_WAIT_MS)
		return () => clearTimeout(timer)
	}, [probedSlide])
	const firstSlideKey = slides[presenter.index]?.slide.id ?? null
	const storyReady = currentStory !== null && resolver.isStoryReady(currentStory)
	const startable =
		storyReady && firstSlideKey !== null && probedSlide === firstSlideKey && (readiness.pending === 0 || probeTimedOut === firstSlideKey)

	// --- Load --------------------------------------------------------------
	useEffect(() => {
		if (!publicToken) return
		let cancelled = false
		presentationService
			.getSharedPresentation(publicToken)
			.then(data => {
				if (cancelled) return
				setShared(data)
				setSnapshot({
					version: data.version,
					name: data.name,
					createdAt: data.publishedAt,
					stats: { slideCount: 0, rasterCount: 0, financialBlockCount: 0 },
					definition: data.definition,
					blocks: data.blocks,
					assets: data.assets ?? {},
				})
				// Only what the screen reads: a name, and that the viewer is not the owner.
				setPresentation({ id: '', name: data.name, isOwner: false, definition: data.definition } as Presentation)
				setStatus('ready')
			})
			.catch(cause => {
				if (cancelled) return
				setLoadError(cause instanceof SharedPresentationLoadError ? cause.reason : 'failed')
				setStatus('failed')
			})
		return () => {
			cancelled = true
		}
	}, [publicToken])

	useEffect(() => {
		if (publicToken) return
		let cancelled = false
		const version = snapshotVersion.current
		Promise.all([presentationService.getPresentation(id), version ? presentationService.getSnapshot(id, version) : null])
			.then(([loaded, frozen]) => {
				if (cancelled) return
				setSnapshot(frozen)
				setPresentation(loaded)
				setStatus('ready')
			})
			.catch(() => !cancelled && setStatus('failed'))
		return () => {
			cancelled = true
		}
	}, [id, publicToken])

	// --- The presenter view in the other window ---------------------------
	const { announceEnded } = usePresentationSync({
		presentationId: id,
		index: presenter.index,
		slideKey: slides[presenter.index]?.slide.id ?? null,
		enabled: started && slides.length > 0 && !snapshot,
		onRemoteGoto: (index, key) => {
			const to = indexOfSlide(slides, key) ?? index
			presenter.go(to, to === presenter.index + 1 ? 'next' : to === presenter.index - 1 ? 'previous' : 'jump')
		},
	})

	const flash = (message: string) => setNotice(message)
	useEffect(() => {
		if (!notice) return
		const timer = setTimeout(() => setNotice(null), NOTICE_MS)
		return () => clearTimeout(timer)
	}, [notice])

	const openPresenterView = () => {
		// The second window follows the live deck, which a snapshot may no
		// longer match slide for slide.
		if (snapshot) return flash('The presenter view is not available for a published snapshot.')
		openingPopupUntil.current = Date.now() + POPUP_GRACE_MS
		const opened = window.open(
			`${presenterViewPath(id)}?slide=${presenter.index + 1}`,
			`mdt-presenter-view-${id}`,
			'popup=yes,width=1280,height=800',
		)
		if (!opened) flash('Allow pop-ups for this site to open the presenter view.')
		else flash('Presenter view opened. Drag it to your own screen.')
	}

	// --- Leaving -----------------------------------------------------------
	const exit = useCallback(() => {
		announceEnded()
		if (document.fullscreenElement) document.exitFullscreen?.().catch(() => undefined)
		// A recipient has nowhere in the app to go: back to the start screen.
		if (publicToken) return setStarted(false)
		if (launchedFrom.current) return navigate(launchedFrom.current)
		navigate(presentation?.isOwner ? presentationEditPath(id) : routes.PRESENTATIONS)
	}, [announceEnded, navigate, presentation?.isOwner, id, publicToken])

	// --- Full screen -------------------------------------------------------
	useEffect(() => {
		const onChange = () => {
			const active = Boolean(document.fullscreenElement)
			setFullscreen(active)
			if (active) {
				enteredFullscreen.current = true
				return
			}
			// Leaving full screen we entered, other than through our own toggle
			// or a presenter view opening, is the browser's Esc: end the show.
			const ownDoing = togglingFullscreen.current || Date.now() < openingPopupUntil.current
			if (enteredFullscreen.current && !ownDoing) exit()
			enteredFullscreen.current = false
			togglingFullscreen.current = false
		}
		document.addEventListener('fullscreenchange', onChange)
		return () => document.removeEventListener('fullscreenchange', onChange)
	}, [exit])

	const toggleFullscreen = () => {
		togglingFullscreen.current = true
		if (document.fullscreenElement) document.exitFullscreen?.().catch(() => undefined)
		else
			rootRef.current?.requestFullscreen?.().catch(() => {
				togglingFullscreen.current = false
			})
	}

	const start = () => {
		setStarted(true)
		// Fire and forget: presenting never waits on this or fails because of it.
		// Looking over a snapshot is not presenting the deck.
		if (!snapshot) presentationService.markPresented(id).catch(() => undefined)
		if (fullscreenAvailable()) rootRef.current?.requestFullscreen?.().catch(() => undefined)
	}

	// --- Frame size --------------------------------------------------------
	useLayoutEffect(() => {
		const measure = () => setFrame(fitFrame(window.innerWidth, window.innerHeight))
		measure()
		window.addEventListener('resize', measure)
		return () => window.removeEventListener('resize', measure)
	}, [])

	useEffect(() => {
		const query = window.matchMedia?.('(prefers-reduced-motion: reduce)')
		if (!query) return
		const update = () => setReducedMotion(query.matches)
		query.addEventListener?.('change', update)
		return () => query.removeEventListener?.('change', update)
	}, [])

	// --- Keyboard ----------------------------------------------------------
	useEffect(() => {
		if (!started) return
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.metaKey || event.ctrlKey || event.altKey) return
			const result = presenter.handleKey(event.key)
			if (result === 'ignored') return
			event.preventDefault()
			if (result === 'exit') exit()
			if (result === 'presenter-view') openPresenterView()
		}
		window.addEventListener('keydown', onKeyDown)
		return () => window.removeEventListener('keydown', onKeyDown)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [started, presenter, exit])

	// Before starting: Enter or Space starts, Esc leaves.
	useEffect(() => {
		if (started) return
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') exit()
			if ((event.key === 'Enter' || event.key === ' ') && startable && slides.length) {
				event.preventDefault()
				start()
			}
		}
		window.addEventListener('keydown', onKeyDown)
		return () => window.removeEventListener('keydown', onKeyDown)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [started, exit, startable, slides.length])

	// --- Chrome and cursor: shown on movement, hidden after a pause --------
	useEffect(() => {
		if (!started || !chromeVisible) return
		const timer = setTimeout(() => setChromeVisible(false), CHROME_HIDE_MS)
		return () => clearTimeout(timer)
	}, [started, chromeVisible])

	// --- Render ------------------------------------------------------------
	const shell = (children: React.ReactNode) => (
		<div
			ref={rootRef}
			className={cn('fixed inset-0 z-[10000] h-[100dvh] w-screen select-none overflow-hidden bg-black text-white', started && !chromeVisible && 'cursor-none')}
			onMouseMove={() => setChromeVisible(true)}
			// No mouse on a tablet: a touch brings the controls back too.
			onTouchStart={() => setChromeVisible(true)}
			data-testid="presenter"
		>
			{children}
		</div>
	)

	// Phones are not served (MYD-498): a slide scaled to a phone's width is too
	// small to present from or to read.
	if (viewer === 'phone') {
		return shell(
			<Centered>
				<p className="mb-1 text-lg font-medium">Open this presentation on a tablet or computer</p>
				<p className="mb-4 max-w-xs text-sm text-white/60">Slides are too small to present on a phone screen.</p>
				{!publicToken && (
					<button type="button" onClick={exit} className="text-sm text-white/70 underline hover:text-white">
						Go back
					</button>
				)}
				{publicToken && <SharedFooter />}
			</Centered>,
		)
	}
	if (status === 'loading') return shell(<Centered>Opening...</Centered>)
	if (status === 'failed' || !presentation || !deck) {
		if (publicToken) {
			return shell(
				<Centered>
					<p className="mb-1 text-lg font-medium" role="alert">
						{SHARE_ERRORS[loadError ?? 'failed']}
					</p>
					<SharedFooter />
				</Centered>,
			)
		}
		return shell(
			<Centered>
				<p className="mb-3">This presentation could not be opened.</p>
				<button type="button" onClick={exit} className="text-sm text-white/70 underline hover:text-white">
					Go back
				</button>
			</Centered>,
		)
	}
	if (slides.length === 0) {
		return shell(
			<Centered>
				<p className="mb-3">This presentation has no slides yet.</p>
				<button type="button" onClick={exit} className="text-sm text-white/70 underline hover:text-white">
					Go back
				</button>
			</Centered>,
		)
	}

	const current = slides[presenter.index]
	const upcoming = slides[presenter.index + 1]
	const firstReady = startable
	const storyCount = deck.stories.length

	if (!started) {
		return shell(
			<Centered>
				<p className="mb-1 text-sm uppercase tracking-wide text-white/50">
					{shared
						? `${shared.sharedBy ? `Shared by ${shared.sharedBy}` : 'Shared presentation'} · ${new Date(shared.publishedAt).toLocaleDateString()}`
						: snapshot
							? `Published version ${snapshot.version}, ${new Date(snapshot.createdAt).toLocaleDateString()}`
							: 'Presentation'}
				</p>
				<h1 className="mb-6 max-w-xl text-3xl font-semibold">{snapshot?.name ?? presentation.name}</h1>
				{firstReady ? (
					<div className="flex flex-col items-center gap-3">
						<button
							type="button"
							onClick={start}
							autoFocus
							className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-base font-medium text-white transition-transform hover:scale-105"
						>
							<Play className="h-5 w-5" />
							{presenter.index > 0 ? `Resume at slide ${presenter.index + 1}` : 'Start presenting'}
						</button>
						{/* Best opened BEFORE going full screen, so it is ready on the
						    laptop screen when the show starts on the projector. */}
						{/* No second screen to drag a window to on a tablet. */}
						{!snapshot && viewer !== 'tablet' && (
							<button type="button" onClick={openPresenterView} className="inline-flex items-center gap-1.5 text-sm text-white/70 hover:text-white">
								<MonitorSmartphone className="h-4 w-4" />
								Open presenter view
							</button>
						)}
					</div>
				) : (
					<p className="text-white/60" role="status">
						Preparing...
					</p>
				)}
				{/* The first slide, drawn off-screen at the real size while the
				    audience waits, so its maps can load their tiles and say so
				    (MYD-515). It also warms the tile cache for the real thing. */}
				{storyReady && current && (
					<div className="pointer-events-none fixed -left-[200vw] top-0" aria-hidden data-testid="readiness-probe">
						<BlockReadinessProvider value={readiness.registrar}>
							<ReadinessProbe key={current.slide.id} slideKey={current.slide.id} onMounted={setProbedSlide}>
								<PresentedSlideView entry={current} width={frame.width || STAGE_WIDTH} blocks={resolver.blocks} theme={deck.theme} storyCount={storyCount} assetName={assetName} />
							</ReadinessProbe>
						</BlockReadinessProvider>
					</div>
				)}
				{notice && <p className="mt-4 text-sm text-white/70">{notice}</p>}
				<p className="mt-6 text-xs text-white/40">
					{viewer === 'tablet'
						? 'Swipe, or tap the right of a slide to turn the page and the left to go back'
						: publicToken
							? 'Arrows or Space to move · S for stories · G for all slides · Esc to stop'
							: 'Arrows or Space to move · S for stories · N for notes · G for all slides · Esc to end'}
				</p>
				{publicToken && <SharedFooter />}
			</Centered>,
		)
	}

	if (viewer === 'tablet') {
		return shell(
			<TouchViewer
				slides={slides}
				presenter={presenter}
				blocks={resolver.blocks}
				theme={deck.theme}
				deckTransition={deck.transition}
				storyCount={storyCount}
				assetName={assetName}
				stories={spans}
				currentStory={storyOnScreen ?? 0}
				reducedMotion={reducedMotion}
				notice={notice}
				showNotes={!publicToken}
				onExit={exit}
			/>,
		)
	}

	const direction = presenter.move && presenter.move.to < presenter.move.from ? -1 : 1
	const transition = transitionFor(presenter.move, slides, deck.transition)

	return shell(
		<>
			<div
				className="absolute inset-0 flex items-center justify-center"
				onClick={() => presenter.overview || presenter.storyMenu || presenter.next()}
				onPointerDown={event => {
					if (event.pointerType !== 'mouse') swipeStart.current = { x: event.clientX, y: event.clientY }
				}}
				onPointerUp={event => {
					const origin = swipeStart.current
					swipeStart.current = null
					if (!origin) return
					const dx = event.clientX - origin.x
					const dy = event.clientY - origin.y
					if (Math.abs(dx) < SWIPE_MIN_PX || Math.abs(dx) < Math.abs(dy)) return
					if (dx < 0) presenter.next()
					else presenter.previous()
				}}
			>
				<div style={{ width: frame.width, height: frame.height }} className="relative">
					<PageFlip slideKey={current.slide.id} transition={transition} direction={direction} reducedMotion={reducedMotion}>
						<PresentedSlideView entry={current} width={frame.width} blocks={resolver.blocks} theme={deck.theme} storyCount={storyCount} assetName={assetName} />
					</PageFlip>
				</div>
			</div>

			{/* The next slide, mounted but off-screen, so its images and tiles
			    are already loaded when it is turned to. */}
			{upcoming && (
				<div className="pointer-events-none fixed -left-[200vw] top-0" aria-hidden>
					<PresentedSlideView entry={upcoming} width={frame.width} blocks={resolver.blocks} theme={deck.theme} storyCount={storyCount} assetName={assetName} />
				</div>
			)}

			{presenter.notes && !publicToken && !current.divider && <SpeakerNotesOverlay notes={current.slide.speakerNotes} />}

			<PresenterChrome
				visible={chromeVisible || presenter.overview || presenter.storyMenu}
				index={presenter.index}
				total={slides.length}
				stories={spans}
				currentStory={storyOnScreen ?? 0}
				typed={presenter.typed}
				notes={presenter.notes}
				notice={notice}
				fullscreen={fullscreen}
				canFullscreen={fullscreenAvailable()}
				onPrevious={presenter.previous}
				onNext={presenter.next}
				onOverview={() => {
					presenter.setStoryMenu(false)
					presenter.setOverview(!presenter.overview)
				}}
				onStoryMenu={() => {
					presenter.setOverview(false)
					presenter.setStoryMenu(!presenter.storyMenu)
				}}
				onToggleNotes={publicToken ? undefined : () => presenter.setNotes(!presenter.notes)}
				onPresenterView={snapshot ? undefined : openPresenterView}
				onToggleFullscreen={toggleFullscreen}
				onExit={exit}
			/>

			{presenter.storyMenu && (
				<StoryMenu
					stories={spans}
					currentStory={storyOnScreen ?? 0}
					onPick={start => {
						presenter.go(start, 'jump')
						presenter.setStoryMenu(false)
					}}
					onClose={() => presenter.setStoryMenu(false)}
				/>
			)}

			{presenter.overview && (
				<OverviewGrid
					slides={slides}
					current={presenter.index}
					blocks={resolver.blocks}
					theme={deck.theme}
					storyCount={storyCount}
					assetName={assetName}
					onPick={index => {
						presenter.go(index, 'jump')
						presenter.setOverview(false)
					}}
					onClose={() => presenter.setOverview(false)}
				/>
			)}
		</>,
	)
}

/**
 * Says when the slide below it has mounted (MYD-515). Effects run children
 * first, so by the time this one runs every block below has registered with
 * the readiness context, and "nothing pending" can be believed.
 */
function ReadinessProbe({ slideKey, onMounted, children }: { slideKey: string; onMounted: (key: string) => void; children: React.ReactNode }) {
	useEffect(() => {
		onMounted(slideKey)
	}, [slideKey, onMounted])
	return <>{children}</>
}

function Centered({ children }: { children: React.ReactNode }) {
	return <div className="flex h-full w-full flex-col items-center justify-center px-6 text-center">{children}</div>
}

/** On a shared link's screens (MYD-509): who made this, for a recipient who has never heard of us. */
function SharedFooter() {
	return (
		<p className="absolute inset-x-0 bottom-0 pb-[max(1.5rem,env(safe-area-inset-bottom))] text-center text-xs text-white/40">
			Shared with{' '}
			<a href="https://mydealteams.com" target="_blank" rel="noopener noreferrer" className="font-medium text-white/60 hover:text-white">
				MyDealTeams
			</a>
		</p>
	)
}

export default PresenterScreen
